import { Contract, formatEther, parseEther } from 'ethers';
import { WorkLobStaking_abi, WorkLobStaking_address, LOB_TOKEN_ABI, LOB_TOKEN_ADDRESS } from './Constants';
import { ensureErc20Allowance } from './erc20Allowance';

/**
 * Get staking contract instance
 */
export const getStakingContract = (signer) => {
  return new Contract(WorkLobStaking_address, WorkLobStaking_abi, signer);
};

/**
 * Get LOB token contract instance
 * @param {Object} signerOrProvider - Signer or provider instance
 */
export const getLOBTokenContract = (signerOrProvider) => {
  if (!signerOrProvider) {
    throw new Error('Signer or provider is required');
  }
  return new Contract(LOB_TOKEN_ADDRESS, LOB_TOKEN_ABI, signerOrProvider);
};

/**
 * Get user's LOB token balance
 * @param {Object} signerOrProvider - Signer or provider instance
 * @param {string} address - Wallet address (optional, will be fetched from signer if not provided)
 */
export const getLOBTokenBalance = async (signerOrProvider, address = null) => {
  try {
    if (!signerOrProvider) {
      throw new Error('Signer or provider is required');
    }
    
    const tokenContract = getLOBTokenContract(signerOrProvider);
    let userAddress = address;
    
    // If address not provided, try to get it from signer
    if (!userAddress && signerOrProvider.getAddress) {
      try {
        userAddress = await signerOrProvider.getAddress();
      } catch (e) {
        console.error('Could not get address from signer:', e);
        throw new Error('Could not determine wallet address');
      }
    }
    
    if (!userAddress) {
      throw new Error('Wallet address is required');
    }
    
    const balance = await tokenContract.balanceOf(userAddress);
    return formatEther(balance);
  } catch (error) {
    console.error('Error getting LOB token balance:', error);
    throw error;
  }
};

/**
 * Approve LOB tokens for staking
 */
export const approveLOBTokens = async (signer, amount) => {
  try {
    // Ensure signer is resolved (ethers v6 getSigner returns a Promise)
    let resolvedSigner = signer;
    if (typeof signer.then === 'function') {
      resolvedSigner = await signer;
    }
    
    // If it's a provider, get signer from it
    if (resolvedSigner && typeof resolvedSigner.getSigner === 'function') {
      resolvedSigner = await resolvedSigner.getSigner();
    }
    
    // Ensure we have a signer
    if (!resolvedSigner || typeof resolvedSigner.getAddress !== 'function') {
      throw new Error('Invalid signer: getAddress method not available');
    }
    
    const tokenContract = getLOBTokenContract(resolvedSigner);
    const amountWei = parseEther(amount.toString());
    const tx = await tokenContract.approve(WorkLobStaking_address, amountWei);
    await tx.wait();
    return tx;
  } catch (error) {
    console.error('Error approving LOB tokens:', error);
    throw error;
  }
};

/**
 * Check if tokens are approved
 */
export const checkTokenApproval = async (signer, amount) => {
  try {
    // Ensure signer is resolved (ethers v6 getSigner returns a Promise)
    let resolvedSigner = signer;
    if (typeof signer.then === 'function') {
      resolvedSigner = await signer;
    }
    
    // If it's a provider, get signer from it
    if (resolvedSigner && typeof resolvedSigner.getSigner === 'function') {
      resolvedSigner = await resolvedSigner.getSigner();
    }
    
    // Ensure we have a signer with getAddress method
    if (!resolvedSigner || typeof resolvedSigner.getAddress !== 'function') {
      throw new Error('Invalid signer: getAddress method not available');
    }
    
    const tokenContract = getLOBTokenContract(resolvedSigner);
    const userAddress = await resolvedSigner.getAddress();
    const amountWei = parseEther(amount.toString());
    const allowance = await tokenContract.allowance(userAddress, WorkLobStaking_address);
    return allowance >= amountWei;
  } catch (error) {
    console.error('Error checking token approval:', error);
    return false;
  }
};

/**
 * Stake tokens (normal staking)
 */
export const stakeTokens = async (signer, amount) => {
  try {
    // Ensure signer is resolved (ethers v6 getSigner returns a Promise)
    let resolvedSigner = signer;
    if (typeof signer.then === 'function') {
      resolvedSigner = await signer;
    }
    
    // If it's a provider, get signer from it
    if (resolvedSigner && typeof resolvedSigner.getSigner === 'function') {
      resolvedSigner = await resolvedSigner.getSigner();
    }
    
    // Ensure we have a signer
    if (!resolvedSigner || typeof resolvedSigner.getAddress !== 'function') {
      throw new Error('Invalid signer: getAddress method not available');
    }
    
    const stakingContract = getStakingContract(resolvedSigner);
    const amountWei = parseEther(amount.toString());
    const tx = await stakingContract.stake(amountWei);
    const receipt = await tx.wait();
    
    // Get stake ID from events
    const stakeEvent = receipt.logs.find(log => {
      try {
        const parsed = stakingContract.interface.parseLog(log);
        return parsed && parsed.name === 'Staked';
      } catch {
        return false;
      }
    });
    
    let stakeId = null;
    if (stakeEvent) {
      const parsed = stakingContract.interface.parseLog(stakeEvent);
      stakeId = parsed.args.stakeId.toString();
    }
    
    return { tx, receipt, stakeId, txHash: receipt.hash };
  } catch (error) {
    console.error('Error staking tokens:', error);
    throw error;
  }
};

/**
 * Stake tokens with lock period
 */
export const stakeLockedTokens = async (signer, amount, lockDays) => {
  try {
    // Ensure signer is resolved (ethers v6 getSigner returns a Promise)
    let resolvedSigner = signer;
    if (typeof signer.then === 'function') {
      resolvedSigner = await signer;
    }
    
    // If it's a provider, get signer from it
    if (resolvedSigner && typeof resolvedSigner.getSigner === 'function') {
      resolvedSigner = await resolvedSigner.getSigner();
    }
    
    // Ensure we have a signer
    if (!resolvedSigner || typeof resolvedSigner.getAddress !== 'function') {
      throw new Error('Invalid signer: getAddress method not available');
    }
    
    const stakingContract = getStakingContract(resolvedSigner);
    const amountWei = parseEther(amount.toString());
    const tx = await stakingContract.stakeLocked(amountWei, lockDays);
    const receipt = await tx.wait();
    
    // Get stake ID from events
    const stakeEvent = receipt.logs.find(log => {
      try {
        const parsed = stakingContract.interface.parseLog(log);
        return parsed && parsed.name === 'Staked';
      } catch {
        return false;
      }
    });
    
    let stakeId = null;
    if (stakeEvent) {
      const parsed = stakingContract.interface.parseLog(stakeEvent);
      stakeId = parsed.args.stakeId.toString();
    }
    
    return { tx, receipt, stakeId, txHash: receipt.hash };
  } catch (error) {
    console.error('Error staking locked tokens:', error);
    throw error;
  }
};

/**
 * Unstake tokens
 */
export const unstakeTokens = async (signer, stakeId) => {
  try {
    const stakingContract = getStakingContract(signer);
    const tx = await stakingContract.unstake(stakeId);
    const receipt = await tx.wait();
    return { tx, receipt, txHash: receipt.hash };
  } catch (error) {
    console.error('Error unstaking tokens:', error);
    throw error;
  }
};

/**
 * Claim rewards (all rewards) - DEPRECATED: Use claimAllRewards instead
 */
export const claimRewards = async (signer) => {
  try {
    const stakingContract = getStakingContract(signer);
    const tx = await stakingContract.claimReward();
    const receipt = await tx.wait();
    return { tx, receipt, txHash: receipt.hash };
  } catch (error) {
    console.error('Error claiming rewards:', error);
    throw error;
  }
};

/**
 * Claim all rewards and reset all stake counters
 */
export const claimAllRewards = async (signer) => {
  try {
    const stakingContract = getStakingContract(signer);
    const tx = await stakingContract.claimAllRewards();
    const receipt = await tx.wait();
    return { tx, receipt, txHash: receipt.hash };
  } catch (error) {
    console.error('Error claiming all rewards:', error);
    throw error;
  }
};

/**
 * Claim a specific amount of rewards (for individual stake claiming)
 */
export const claimRewardAmount = async (signer, amount) => {
  try {
    const stakingContract = getStakingContract(signer);
    const amountWei = parseEther(amount.toString());
    const tx = await stakingContract.claimRewardAmount(amountWei);
    const receipt = await tx.wait();
    return { tx, receipt, txHash: receipt.hash };
  } catch (error) {
    console.error('Error claiming reward amount:', error);
    throw error;
  }
};

/**
 * Claim reward for a specific stake (resets the stake's reward counter)
 */
export const claimStakeReward = async (signer, stakeId) => {
  try {
    const stakingContract = getStakingContract(signer);
    const tx = await stakingContract.claimStakeReward(stakeId);
    const receipt = await tx.wait();
    return { tx, receipt, txHash: receipt.hash };
  } catch (error) {
    console.error('Error claiming stake reward:', error);
    throw error;
  }
};

/**
 * Get pool information
 */
export const getPoolInfo = async (provider) => {
  try {
    const stakingContract = new Contract(WorkLobStaking_address, WorkLobStaking_abi, provider);
    const poolInfo = await stakingContract.getPoolInfo();
    
    // Also get total staked
    const totalStaked = await stakingContract.totalStaked();
    
    return {
      totalPoolBalance: formatEther(poolInfo.totalPoolBalance),
      currentRewardRate: formatEther(poolInfo.currentRewardRate),
      periodEndTime: poolInfo.periodEndTime.toString(),
      apy: poolInfo.apy.toString(),
      totalStaked: formatEther(totalStaked)
    };
  } catch (error) {
    console.error('Error getting pool info:', error);
    throw error;
  }
};

/**
 * Get user's staking details
 */
export const getUserStakingDetails = async (provider, userAddress) => {
  try {
    const stakingContract = new Contract(WorkLobStaking_address, WorkLobStaking_abi, provider);
    const details = await stakingContract.getStakerDetails(userAddress);
    
    return {
      walletAddress: details[0],
      totalStaked: formatEther(details[1]),
      totalLocked: formatEther(details[2]),
      claimableRewards: formatEther(details[3]),
      hasLocked: details[4]
    };
  } catch (error) {
    console.error('Error getting user staking details:', error);
    throw error;
  }
};

/**
 * Get user's locked staking balance (for voting eligibility)
 */
export const getLockedStakingBalance = async (provider, userAddress) => {
  try {
    const stakingContract = new Contract(WorkLobStaking_address, WorkLobStaking_abi, provider);
    const lockedBalance = await stakingContract.lockedBalances(userAddress);
    return formatEther(lockedBalance);
  } catch (error) {
    console.error('Error getting locked staking balance:', error);
    return '0';
  }
};

/**
 * Get user's stake IDs
 */
export const getUserStakeIds = async (provider, userAddress) => {
  try {
    const stakingContract = new Contract(WorkLobStaking_address, WorkLobStaking_abi, provider);
    const stakeIds = await stakingContract.getUserStakes(userAddress);
    return stakeIds.map(id => id.toString());
  } catch (error) {
    console.error('Error getting user stake IDs:', error);
    throw error;
  }
};

/**
 * Get stake details by ID
 */
export const getStakeDetails = async (provider, stakeId) => {
  try {
    const stakingContract = new Contract(WorkLobStaking_address, WorkLobStaking_abi, provider);
    const details = await stakingContract.getStakeDetails(stakeId);
    
    return {
      id: details.id.toString(),
      staker: details.staker,
      amount: formatEther(details.amount),
      stakedAt: details.stakedAt.toString(),
      unlockTime: details.unlockTime.toString(),
      isLocked: details.isLocked,
      isActive: details.isActive,
      rewardPerTokenPaid: details.rewardPerTokenPaid ? details.rewardPerTokenPaid.toString() : '0'
    };
  } catch (error) {
    console.error('Error getting stake details:', error);
    throw error;
  }
};

/**
 * Get all stakers (for overview) - DEPRECATED: Use getAllActiveStakes instead
 */
export const getAllStakers = async (provider) => {
  try {
    const stakingContract = new Contract(WorkLobStaking_address, WorkLobStaking_abi, provider);
    const result = await stakingContract.getAllStakers();
    
    const stakers = [];
    for (let i = 0; i < result[0].length; i++) {
      stakers.push({
        address: result[0][i],
        stakedAmount: formatEther(result[1][i]),
        rewardsEarned: formatEther(result[2][i]),
        lockedAmount: formatEther(result[3][i]),
        hasLocked: result[4][i]
      });
    }
    
    return stakers;
  } catch (error) {
    console.error('Error getting all stakers:', error);
    throw error;
  }
};

/**
 * Get all active individual stakes (for overview table)
 */
export const getAllActiveStakes = async (provider) => {
  try {
    const stakingContract = new Contract(WorkLobStaking_address, WorkLobStaking_abi, provider);
    const result = await stakingContract.getAllActiveStakes();
    
    const stakes = [];
    for (let i = 0; i < result[0].length; i++) {
      const stakeId = result[0][i].toString();
      
      // Get individual stake reward
      let stakeReward = '0';
      try {
        const reward = await stakingContract.getStakeReward(stakeId);
        stakeReward = formatEther(reward);
      } catch (error) {
        console.error(`Error getting reward for stake ${stakeId}:`, error);
      }
      
      stakes.push({
        stakeId: stakeId,
        address: result[1][i],
        walletAddress: result[1][i],
        staker: result[1][i],
        amount: formatEther(result[2][i]),
        stakedAmount: formatEther(result[2][i]),
        stakedAt: result[3][i].toString(),
        unlockTime: result[4][i].toString(),
        isLocked: result[5][i],
        hasLocked: result[5][i],
        isActive: result[6][i],
        rewardsEarned: stakeReward
      });
    }
    
    return stakes;
  } catch (error) {
    console.error('Error getting all active stakes:', error);
    throw error;
  }
};

/**
 * Calculate estimated rewards
 * @param {number|string} amount - Amount to stake
 * @param {number|string} rewardRate - Reward rate per second (in wei, will be converted)
 * @param {number|string} totalStaked - Total amount currently staked (in wei, will be converted)
 * @param {number|string} days - Number of days
 */
export const calculateEstimatedRewards = (amount, rewardRate, totalStaked, days) => {
  try {
    // Convert to numbers for calculation
    const amountNum = parseFloat(amount) || 0;
    const rewardRateNum = parseFloat(rewardRate) || 0;
    const totalStakedNum = parseFloat(totalStaked) || 0;
    const daysNum = parseFloat(days) || 0;
    
    // If no amount or invalid inputs, return 0
    if (amountNum <= 0 || daysNum <= 0) {
      return '0.0000';
    }
    
    // If no reward rate, return 0
    if (rewardRateNum <= 0) {
      return '0.0000';
    }
    
    // Calculate seconds
    const seconds = daysNum * 24 * 60 * 60;
    
    // If no one has staked yet, assume the new stake will be the only one
    // So user gets 100% of rewards
    if (totalStakedNum <= 0) {
      // User's reward = rewardRate * seconds (they get all rewards)
      const estimatedReward = rewardRateNum * seconds;
      return estimatedReward.toFixed(4);
    }
    
    // Reward rate is tokens per second distributed to all stakers
    // User's share of rewards per second = (userAmount / (totalStaked + userAmount)) * rewardRate
    // Total reward over period = (userAmount / (totalStaked + userAmount)) * rewardRate * seconds
    // Note: We use (totalStaked + amount) to account for the new stake
    const newTotalStaked = totalStakedNum + amountNum;
    const userShare = amountNum / newTotalStaked;
    const estimatedReward = userShare * rewardRateNum * seconds;
    
    return estimatedReward.toFixed(4);
  } catch (error) {
    console.error('Error calculating estimated rewards:', error);
    return '0.0000';
  }
};

// Deployer functions
/**
 * Fund staking pool (deployer only)
 * @param {Object} signer - Signer instance
 * @param {string|number} rewardAmount - Amount in LOB tokens (not wei)
 * @param {number} duration - Duration in seconds
 */
export const fundPool = async (signer, rewardAmount, duration) => {
  try {
    const amountWei = parseEther(rewardAmount.toString());
    await ensureErc20Allowance(
      signer,
      LOB_TOKEN_ADDRESS,
      LOB_TOKEN_ABI,
      WorkLobStaking_address,
      amountWei
    );

    const stakingContract = getStakingContract(signer);
    try {
      await stakingContract.fundPool.staticCall(amountWei, duration);
    } catch (pre) {
      const msg = pre?.shortMessage || pre?.message || '';
      if (msg.includes('Not enough tokens for reward')) {
        throw new Error(
          'Staking pool rejected this funding schedule. If a reward period is already active, wait until it ends or fund with enough LOB to cover the blended reward rate. Alternatively try again after the current period finishes.'
        );
      }
      if (msg.includes('Transfer failed')) {
        throw new Error(
          'LOB transfer failed. Confirm you have enough LOB balance and try again after any pending approval confirms.'
        );
      }
      throw pre;
    }

    const tx = await stakingContract.fundPool(amountWei, duration);
    const receipt = await tx.wait();
    return { tx, receipt, txHash: receipt.hash };
  } catch (error) {
    console.error('Error funding pool:', error);
    throw error;
  }
};

/**
 * Add tokens to pool (deployer only)
 * @param {Object} signer - Signer instance
 * @param {string|number} amount - Amount in LOB tokens (not wei)
 */
export const addToPool = async (signer, amount) => {
  try {
    const amountWei = parseEther(amount.toString());
    await ensureErc20Allowance(
      signer,
      LOB_TOKEN_ADDRESS,
      LOB_TOKEN_ABI,
      WorkLobStaking_address,
      amountWei
    );

    const stakingContract = getStakingContract(signer);
    await stakingContract.addToPool.staticCall(amountWei);
    const tx = await stakingContract.addToPool(amountWei);
    const receipt = await tx.wait();
    return { tx, receipt, txHash: receipt.hash };
  } catch (error) {
    console.error('Error adding to pool:', error);
    throw error;
  }
};

/**
 * Withdraw from pool (deployer only)
 */
export const withdrawFromPool = async (signer, amount) => {
  try {
    const stakingContract = getStakingContract(signer);
    const amountWei = parseEther(amount.toString());
    const tx = await stakingContract.withdrawFromPool(amountWei);
    const receipt = await tx.wait();
    return { tx, receipt, txHash: receipt.hash };
  } catch (error) {
    console.error('Error withdrawing from pool:', error);
    throw error;
  }
};
